import React, { useContext, useMemo } from 'react'
import PropTypes from 'prop-types'
import { ThemeProvider } from 'emotion-theming'
import { Global, css } from '@emotion/core'
import { TPThemeContext } from 'portal-modules/common/context/theme-context'
import { blueTheme, lightTheme } from '../../common/theme'
import setGlobalStyles from '../../common/set-global-styles'

/**
 * ThemeWrapper Component
 * Wraps the application with the appropriate theme (dark or light) based on user preference.
 * Applies global styles, high contrast mode, and provides theme context to all child components.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to be wrapped with theme
 * @param {Object} props.brandingSettings - Branding configuration settings for theme customization
 */
const ThemeWrapper = ({ children, brandingSettings }) => {
  const { dark, fontFamily, fontSize, fontWeight, highContrast, cardStyle, cornerRadius, primaryColor, secondaryColor, accentColor, iconStyle, spacingDensity } = useContext(TPThemeContext)
  
  // Memoize theme computation to avoid unnecessary recalculations
  // Pass highContrast, cardStyle, and cornerRadius flags to theme functions to apply appropriate colors and styles
  const theme = useMemo(() => {
    // Create theme with custom color overrides passed as parameters
    // If no custom colors are provided, the theme functions will use their default parameter values
    const themeParams = {
      ...brandingSettings,
      highContrast,
      cardStyle,
      cornerRadius,
      // Override theme parameters with custom colors if they exist
      // The theme functions have default values: button_primary = '#6366f1' (dark) / '#641083' (light)
      ...(primaryColor && { button_primary: primaryColor }),
      // The theme functions have default values: button_secondary = '#252e42' (dark) / undefined (light)
      ...(secondaryColor && { button_secondary: secondaryColor }),
      // Note: accent color (sidebar_accent) is handled separately as it's not a theme parameter
    }
    
    const baseTheme = dark ? blueTheme(themeParams) : lightTheme(themeParams)
    
    // Apply additional overrides that aren't theme parameters
    return {
      ...baseTheme,
      // Override icon.primary with custom primary color (affects both themes)
      icon: {
        ...baseTheme.icon,
        primary: primaryColor || baseTheme.icon?.primary
      },
      // Override text.sidebar_accent with custom accent color (affects both themes)
      text: {
        ...baseTheme.text,
        sidebar_accent: accentColor || baseTheme.text?.sidebar_accent
      },
      // Override secondary.base with custom secondary color (affects both themes)
      secondary: {
        ...baseTheme.secondary,
        base: secondaryColor || baseTheme.secondary?.base
      }
    }
  }, [dark, brandingSettings, primaryColor, secondaryColor, accentColor, highContrast, cardStyle, cornerRadius])

  // Memoize global styles with font preferences, icon style, and spacing density
  const globalStyles = useMemo(() => {
    return css`
      ${setGlobalStyles(theme, fontFamily, fontSize, fontWeight, iconStyle, spacingDensity)};
    `
  }, [theme, fontFamily, fontSize, fontWeight, iconStyle, spacingDensity])

  return (
    <>
      <Global styles={globalStyles} />
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </>
  )
}

ThemeWrapper.propTypes = {
  /** Child components to be rendered within the theme context */
  children: PropTypes.node.isRequired,
  /** Branding settings object used to customize theme colors and styles */
  brandingSettings: PropTypes.object.isRequired,
}

export default ThemeWrapper

